import { useMemo, useState } from 'react';

import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useGetStudentAnswersPollingFallback } from 'approot/activity-builder/classroom/use-get-student-answers';
import { useGetClassStudents } from 'approot/students/use-get-class-students';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
  ClassroomAnswerInstruction,
} from 'approot/activity-builder/classroom/classroom-answer.style';
import { ClassroomStudentIdentifierWithVisibility } from 'approot/activity-builder/classroom/classroom-student-identifier';
import { ClassAnswerNoStudents } from 'approot/activity-builder/classroom/class-answer-empty';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import { useFindComponentDimensions } from 'approot/activity-builder/components/component.hooks';
import { COMPONENT_SELECTOR } from 'approot/activity-builder/components/shared/logic/get-component-selector-id';
import ClassroomAwaitingResponse from 'approot/activity-builder/classroom/classroom-awaiting';
import ClassroomAnswerDetailHeader from 'approot/activity-builder/classroom/classroom-answer-detail-header';
import ClassroomAnswerStudentVisibility from 'approot/activity-builder/classroom/classroom-answer-student-visibility';
import { Box } from 'approot/shared/design-system/box/box.style';
import { ViewProps } from '../fill-in-blanks.constants';
import {
  getStudentFillInBlanksAnswers,
  hasStudentResponseAnswer,
} from './fill-in-blanks-class.utils';
import FillInBlanksViewAnswers from '../fill-in-blanks-view-answers';

const FillInBlanksIndividual = (props: ViewProps) => {
  const { id, subActivityProperties } = props;

  const [activeStudent, setActiveStudent] = useState<number>(0);

  const { userClassId } = useActivityBuilderPresentContext();
  const { width } = useFindComponentDimensions(id, COMPONENT_SELECTOR);

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    fetchPolicy: 'cache-first',
    classId: Number(userClassId),
  });

  const { data, error: studentAnswersError } =
    useGetStudentAnswersPollingFallback({
      userClassId: Number(userClassId),
      componentId: id,
    });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const studentAnswers = useMemo(
    () => data?.getClassStudentsAnswers || [],
    [data]
  );

  const results = useMemo(() => {
    const students =
      activeStudent > 0
        ? studentsInClass.filter(s => s.id === activeStudent)
        : studentsInClass;

    return getStudentFillInBlanksAnswers({
      studentAnswers,
      studentsInClass: students,
      componentId: id,
    });
  }, [activeStudent, studentsInClass, studentAnswers, id]);

  if (studentsLoading || !studentAnswers) {
    return <Bouncer />;
  }

  if (studentAnswersError || studentsError) {
    return (
      <ErrorMessageActionable
        ariaPage="Fill in the Blanks responses"
        paddingTop={0}
      />
    );
  }

  if (studentsInClass.length === 0) {
    return <ClassAnswerNoStudents />;
  }

  return (
    <ClassroomAnswerInstruction>
      {activeStudent > 0 ? (
        <ActivitySheetInnerMax>
          <ClassroomAnswerDetailHeader
            studentId={activeStudent}
            students={studentsInClass}
            onSelect={setActiveStudent}
          />
          <SubActivityView subActivityDetails={subActivityProperties}>
            {hasStudentResponseAnswer(results[0]) ? (
              <FillInBlanksViewAnswers
                {...props}
                fieldAnswers={results[0].answers}
              />
            ) : (
              <ClassroomAwaitingResponse />
            )}
          </SubActivityView>
        </ActivitySheetInnerMax>
      ) : (
        <SubActivityView subActivityDetails={subActivityProperties}>
          <Box $justifyContent="flex-end" $marginBottom={24}>
            <ClassroomAnswerStudentVisibility />
          </Box>
          <ClassroomAnswerGrid>
            {results.map(result => (
              <ClassroomAnswerGridItem key={result.studentId}>
                <ClassroomAnswerGridInner>
                  {hasStudentResponseAnswer(result) ? (
                    <>
                      <ClassroomAnswerGridButton
                        type="button"
                        onClick={() => setActiveStudent(result.studentId)}
                      >
                        <ScreenReaderOnly>
                          {`View ${result.studentName}'s response`}
                        </ScreenReaderOnly>
                      </ClassroomAnswerGridButton>
                      <ActivityBuilderComponentPreview
                        content={{
                          width,
                          height: 232,
                        }}
                      >
                        <FillInBlanksViewAnswers
                          {...props}
                          fieldAnswers={result.answers}
                        />
                      </ActivityBuilderComponentPreview>
                    </>
                  ) : (
                    <ClassroomAwaitingResponse />
                  )}
                </ClassroomAnswerGridInner>
                <ClassroomStudentIdentifierWithVisibility
                  name={result.studentName}
                  emoji={result.studentEmoji}
                />
              </ClassroomAnswerGridItem>
            ))}
          </ClassroomAnswerGrid>
        </SubActivityView>
      )}
    </ClassroomAnswerInstruction>
  );
};

export default FillInBlanksIndividual;
